import { memo, useLayoutEffect, useRef } from 'react';
import { useIsExportFrozen } from '@components/MainStage/exportFreeze.store';
import { ReactFlowRendererSelector } from '@constants/entities/reports';

import { FrozenCanvasStyled } from './FrozenCanvas.styled';

const VIEWPORT_CONTAINER_CLASS = 'react-flow__container';

/**
 * The snapshot duplicates canvas markup.
 */
const detachFromCanvasLookups = (snapshot: HTMLElement) => {
  [snapshot, ...snapshot.querySelectorAll<HTMLElement>('*')].forEach((node) => {
    node.removeAttribute('id');
    node.removeAttribute('data-id');

    if (!node.classList.contains(VIEWPORT_CONTAINER_CLASS)) return;

    node.classList.remove(VIEWPORT_CONTAINER_CLASS);
    Object.assign(node.style, {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: '0px',
      left: '0px',
    });
  });
};

const CanvasSnapshot = () => {
  const holderRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const holder = holderRef.current;
    const source = document.querySelector<HTMLElement>(
      ReactFlowRendererSelector,
    );

    if (!holder || !source) return undefined;

    const holderBox = holder.getBoundingClientRect();
    const sourceBox = source.getBoundingClientRect();
    const snapshot = source.cloneNode(true) as HTMLElement;

    detachFromCanvasLookups(snapshot);
    snapshot.setAttribute('aria-hidden', 'true');

    Object.assign(snapshot.style, {
      position: 'absolute',
      left: `${sourceBox.left - holderBox.left}px`,
      top: `${sourceBox.top - holderBox.top}px`,
      width: `${sourceBox.width}px`,
      height: `${sourceBox.height}px`,
    });

    holder.appendChild(snapshot);

    return () => snapshot.remove();
  }, []);

  return <FrozenCanvasStyled ref={holderRef} />;
};

export const FrozenCanvas = memo(function FrozenCanvas() {
  const isFrozen = useIsExportFrozen();

  if (!isFrozen) return null;

  return <CanvasSnapshot />;
});
